import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./authContext";
import { useOptions } from "./optionContext";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants";
import { getCustomSitesData, getUserAllocations } from "../services/manageSitesService";
import { getCustomerLocationOptionFlags, resolveServiceOptions } from "../utilities/customerLocationSettings";
import { setAnalyticsContext } from "../utilities/analyticsService";
import formatCurrency from "../utilities/currency";
// Create the context
const SiteContext = createContext();

// Custom hook to use the context
export const useSite = () => useContext(SiteContext);

const GLOBAL_SITE_OPTION = {
	site_id: 0, // A unique ID that won't conflict with real site IDs
	customer_site_number: "GLOBAL",
	customer_site_name: "All Sites",
	parent_customer_name: "Global",
	parent_customer_number: "GLOBAL",
	site_legal_entity: "GLOBAL",
	site_currency: "USD",
	site_name: "All Sites",
	warehouse: "GLOBAL",
	services_meta: {},
};

const DEFAULT_SITE_CURRENCY = "USD";
const DEFAULT_SITE_LEGAL_ENTITY = "AUSA";
const INVALID_META_VALUES = new Set(["", "null", "undefined", "n/a", "na"]);

const buildAnalyticsSiteContext = (site = {}) => ({
	customer_id: site.cus_id ?? site.customer_id ?? "",
	customer_name: site.parent_customer_name ?? site.customer_name ?? "",
	site_id: site.site_id ?? "",
	site_name: site.customer_site_name ?? site.site_name ?? "",
	location: site.address_name_ln ?? site.location ?? site.region ?? site.country ?? "",
	user_role: "",
});

// Create the provider component
export const SiteProvider = ({ children }) => {
	const [selectedSite, setSelectedSite] = useState(""); // Initially empty
	const [selectedSiteKey, setSelectedSiteKey] = useState("");
	const [selectedSiteData, setSelectedSiteData] = useState([]); // Initially empty
	const [siteOptions, setSiteOptions] = useState([]); // State to hold the site options
	const [siteData, setSiteData] = useState([]); // State to hold the site options
	const [loading, setLoading] = useState(true); // State for loading
	const [counter, setCounter] = useState(0);
	const [selectedSiteCurrency, setSelectedSiteCurrency] = useState(DEFAULT_SITE_CURRENCY.toLowerCase());
	const [selectedSiteLegalEnitity, setSelectedSiteLegalEnitity] = useState(DEFAULT_SITE_LEGAL_ENTITY);
	const [selectedSiteWarehouse, setSelectedSiteWarehouse] = useState("");
	const [selectedSiteSName, setSelectedSiteSName] = useState("");
	const [currencySymbol, setCurrencySymbol] = useState("$");
	const [isAdminAllSites, setIsAdminAllSites] = useState(false);
	const [loggedinUser, setLoggedinUser] = useState(() => {
		const sessionUser = sessionStorage.getItem("user"); // Store user data in sessionStorage
		return sessionUser ? JSON.parse(sessionUser) : null;
	});

	const navigate = useNavigate();
	const { user } = useAuth();
	const { optionKeys } = useOptions();

	const location = useLocation();
	const pathnames = location.pathname.split("/").filter((x) => x);

	const normalizeServicesMeta = (rawMeta) => {
		if (!rawMeta) return {};
		if (typeof rawMeta === "string") {
			try {
				return JSON.parse(rawMeta);
			} catch {
				return {};
			}
		}
		return rawMeta;
	};

	const getDefaultCurrencyFromOptions = () => {
		const normalized = String(optionKeys?.currency ?? "").trim().toUpperCase();
		if (!normalized || INVALID_META_VALUES.has(normalized.toLowerCase())) {
			return DEFAULT_SITE_CURRENCY;
		}
		return normalized;
	};

	const getDefaultLegalEntityFromOptions = () => {
		const candidates = [
			optionKeys?.default_legal_entity,
			optionKeys?.defaultLegalEntity,
			optionKeys?.legalEntity,
			optionKeys?.defaultlegalentity,
		];
		for (const candidate of candidates) {
			const normalized = String(candidate ?? "").trim().toUpperCase();
			if (normalized && !INVALID_META_VALUES.has(normalized.toLowerCase())) {
				return normalized;
			}
		}
		return DEFAULT_SITE_LEGAL_ENTITY;
	};

	const normalizeSiteCurrency = (value) => {
		const normalized = String(value ?? "").trim().toUpperCase();
		if (INVALID_META_VALUES.has(normalized.toLowerCase())) {
			return getDefaultCurrencyFromOptions();
		}
		return normalized || getDefaultCurrencyFromOptions();
	};

	const normalizeSiteLegalEntity = (value) => {
		const normalized = String(value ?? "").trim().toUpperCase();
		if (INVALID_META_VALUES.has(normalized.toLowerCase())) {
			return getDefaultLegalEntityFromOptions();
		}
		return normalized || getDefaultLegalEntityFromOptions();
	};

	const normalizeSiteRecord = (site) => {
		if (!site || typeof site !== "object") return site;
		return {
			...site,
			site_currency: normalizeSiteCurrency(site.site_currency),
			site_legal_entity: normalizeSiteLegalEntity(site.site_legal_entity),
			services_meta: normalizeServicesMeta(site.services_meta),
		};
	};

	const resolveCustomerIdForSite = async (site) => {
		if (!site?.site_id) return site;
		if (Number.isFinite(Number(site?.cus_id)) && Number(site.cus_id) > 0) {
			return site;
		}

		try {
			const query = `
				SELECT cust_id
				FROM customer_sites
				WHERE site_id = ${Number(site.site_id)}
				ORDER BY id
				LIMIT 1
			`;
			const response = await getCustomSitesData(query);
			const resolvedCusId = Number(response?.data?.[0]?.cust_id);
			if (Number.isFinite(resolvedCusId) && resolvedCusId > 0) {
				return {
					...site,
					cus_id: resolvedCusId,
				};
			}
		} catch (error) {
			console.error("Failed to resolve customer id for site:", error);
		}

		return site;
	};

	const getCurrencySymbol = (selectedCurrency) => {
		const currencySymbols = {
			USD: "$", // US Dollar
			CAD: "CA$", // Canadian Dollar
			GBP: "£", // British Pound
			EUR: "€", // Euro
			SGD: "S$", // Singapore Dollar
			INR: "₹", // Indian Rupee
		};

		if (!selectedCurrency) {
			return currencySymbols["USD"];
		}

		const trimmedCurrency = selectedCurrency.trim().toUpperCase();
		const symbol = currencySymbols[trimmedCurrency];

		if (!symbol) {
			return currencySymbols["USD"];
		}

		return symbol;
	};

	useEffect(() => {
		if (loggedinUser) {
			fetchSites();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedinUser]);

	useEffect(() => {
		if (loggedinUser && optionKeys) {
			fetchSites();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionKeys]);

	useEffect(() => {
		setLoggedinUser(user);
	}, [user]);

	useEffect(() => {
		if (!selectedSiteData || Array.isArray(selectedSiteData)) return;
		setAnalyticsContext(buildAnalyticsSiteContext(selectedSiteData));
	}, [selectedSiteData]);

	const fetchSites = async () => {
		try {
			const { servicesEnabled } = getCustomerLocationOptionFlags(optionKeys);
			const serviceOptions = resolveServiceOptions(optionKeys);
			// Fetch user allocations from backend (user_sites table)
			const uid = (user?.user_id || loggedinUser?.user_id);
			if (!uid) return;
			const allocResp = await getUserAllocations(uid);
			const allocations = allocResp?.data?.allocations || [];
			const normalizedAllocations = allocations
				.map((a) => ({
					site_id: Number(a?.site_id),
					cus_id: Number(a?.cus_id),
				}))
				.filter((a) => Number.isFinite(a.site_id) && a.site_id > 0 && Number.isFinite(a.cus_id) && a.cus_id > 0);
			const siteIds = [...new Set(normalizedAllocations.map((a) => a.site_id))];

			// siteContext.js (inside fetchSites)
			const currentUser = user || loggedinUser;

			const roleTypeOrName =
				currentUser?.role?.role_type ||
				currentUser?.role?.role_name ||
				currentUser?.user_role_name ||   // backend sometimes provides this
				currentUser?.user_role;          // last fallback

			const isSuperAdmin = currentUser?.user_id === 1;
			const isAdmin =
				isSuperAdmin || String(roleTypeOrName || "").toLowerCase() === "admin";
			const isSuperAdminAllSites = isSuperAdmin && normalizedAllocations.length === 0;

			let whereClause = '1=0';
			let allocationJoin = '';
			if (isSuperAdminAllSites) {
				whereClause = '1=1';
			} else if (normalizedAllocations.length > 0) {
				allocationJoin = `JOIN user_sites us ON us.site_id = cs.site_id AND us.cus_id = cs.cust_id AND us.user_id = ${Number(uid)}`;
				whereClause = `(${normalizedAllocations
					.map((a) => `(cs.site_id = ${a.site_id} AND cs.cust_id = ${a.cus_id})`)
					.join(' OR ')})`;
			} else if (isAdmin) {
				whereClause = '1=1';
			}
			setIsAdminAllSites(isAdmin && siteIds.length === 0);


			const serviceMetaKeys = servicesEnabled
				? serviceOptions.map((service) => service?.key).filter(Boolean)
				: [];
			const serviceMetaClause = serviceMetaKeys.length
				? `, COALESCE((
                        SELECT json_object_agg(meta_key, meta_value)
                        FROM site_meta
                        WHERE site_id = s.site_id AND meta_key IN (${serviceMetaKeys.map((key) => `'${key}'`).join(",")})
                    ), '{}'::json) as services_meta`
				: ", '{}'::json as services_meta";

			let query = `SELECT
						cs.id AS customer_site_link_id,
						s.*,
						cs.cust_id AS cus_id,
						c.customer_number AS parent_customer_number,
						c.customer_name AS parent_customer_name,
						COALESCE(c.oem_share_approved, 0) AS oem_share_approved,
						sm.meta_value as site_legal_entity,
						sm1.meta_value as site_currency,
						sm2.meta_value as site_name,
						sm3.meta_value as warehouse
						${serviceMetaClause}
                FROM customer_sites as cs
                JOIN sites as s ON cs.site_id = s.site_id
                ${allocationJoin}
                LEFT JOIN customers c ON cs.cust_id = c.cus_id
                LEFT JOIN site_meta as sm ON s.site_id=sm.site_id AND sm.meta_key='site_legal_entity'
                LEFT JOIN site_meta as sm1 ON s.site_id=sm1.site_id AND sm1.meta_key='site_currency'
                LEFT JOIN site_meta as sm2 ON s.site_id=sm2.site_id AND sm2.meta_key='site_name'
                LEFT JOIN site_meta as sm3 ON s.site_id=sm3.site_id AND sm3.meta_key='warehouse'
                WHERE ${whereClause}
                ORDER BY c.customer_name ASC NULLS LAST, s.customer_site_number ASC, cs.id ASC`;

			const response = await getCustomSitesData(query);
			let data = response.data;

			// If isAdmin and whereClause was '1=1' (meaning all sites access) but no data came back,
			// inject a dummy GLOBAL_SITE_OPTION
			if (isAdmin && whereClause === '1=1' && (!Array.isArray(data) || data.length === 0)) {
				data = [GLOBAL_SITE_OPTION];
			}

			const normalizedData = Array.isArray(data) ? data.map(normalizeSiteRecord) : [];
			const optionSource = normalizedData;

			let siteOptions = optionSource.map((site) => {
				const displaySiteLabel =
					[
						String(site.customer_site_name || "").trim(),
						String(site.address_name_ln || "").trim(),
					]
						.filter(Boolean)
						.join(" - ") ||
					String(site.site_name || "").trim() ||
					String(site.address_name_ln || "").trim();
				
				const searchTokens = [
					site.customer_site_name,
					site.customer_site_number,
					site.site_name,
					site.address_name_ln,
					site.parent_customer_name,
					site.parent_customer_number,
				]
					.filter((token) => token !== undefined && token !== null && token !== "")
					.map((token) => String(token).toLowerCase())
					.join(" | ");

				return {
					key: `${site.site_id}:${site.cus_id ?? ""}`,
					selectionKey: `${site.site_id}:${site.cus_id ?? ""}`,
					site_id: site.site_id,
					cus_id: site.cus_id,
					value: site.customer_site_number,
					text: displaySiteLabel,
					site_name: site.site_name,
					parent_customer_name: site.parent_customer_name,
					parent_customer_number: site.parent_customer_number,
					oem_share_approved: site.oem_share_approved,
					customer_site_name: site.customer_site_name,
					customer_site_number: site.customer_site_number,
					address_name_ln: site.address_name_ln,
					customer_name: site.parent_customer_name,
					customer_number: site.parent_customer_number,
					searchTokens,
					services_meta: site.services_meta,
				};
			});

			// Ensure GLOBAL_SITE_OPTION has correct metadata after mapping
			if (Array.isArray(data) && data.some(s => s.customer_site_number === GLOBAL_SITE_OPTION.customer_site_number)) {
				const globalOptionIndex = siteOptions.findIndex(opt => opt.value === GLOBAL_SITE_OPTION.customer_site_number);
				if (globalOptionIndex !== -1) {
					siteOptions[globalOptionIndex] = {
						...siteOptions[globalOptionIndex],
						site_legal_entity: GLOBAL_SITE_OPTION.site_legal_entity,
						site_currency: GLOBAL_SITE_OPTION.site_currency,
						site_name: GLOBAL_SITE_OPTION.site_name,
						warehouse: GLOBAL_SITE_OPTION.warehouse,
						services_meta: GLOBAL_SITE_OPTION.services_meta,
					};
				}
			}


			setSiteOptions(siteOptions);
			setSiteData(normalizedData);
			setCounter(counter + 1);
			const storedSite = localStorage.getItem("selectedSite");
			const storedSiteKey = localStorage.getItem("selectedSiteKey");
			let initialSelectedSiteValue = "";
			let initialSelectedSiteData = null;

			if (storedSiteKey) {
				initialSelectedSiteData = normalizedData.find(
					(site) => `${site.site_id}:${site.cus_id ?? ""}` === storedSiteKey
				);
				initialSelectedSiteValue = initialSelectedSiteData?.customer_site_number || "";
			}

			if (!initialSelectedSiteData && storedSite && siteOptions.some((site) => site.value === storedSite)) {
				initialSelectedSiteValue = storedSite;
			}

			if (!initialSelectedSiteData && !initialSelectedSiteValue && siteOptions.length > 0) {
				initialSelectedSiteData = normalizedData.find(
					(site) =>
						`${site.site_id}:${site.cus_id ?? ""}` ===
						(siteOptions[0]?.selectionKey || "")
				);
				initialSelectedSiteValue =
					initialSelectedSiteData?.customer_site_number || siteOptions[0].value;
			}

			if (initialSelectedSiteValue) {
				if (!initialSelectedSiteData) {
					initialSelectedSiteData =
						(storedSiteKey
							? normalizedData.find(
									(site) =>
										`${site.site_id}:${site.cus_id ?? ""}` === storedSiteKey
							  )
							: null) ||
						(() => {
							const matches = normalizedData.filter(
								(site) => site.customer_site_number === initialSelectedSiteValue
							);
							return matches.length === 1 ? matches[0] : null;
						})() ||
						(() => {
							// When site numbers are duplicated across customers, pick a concrete option
							// from siteOptions so selected site+customer context is never left undefined.
							const optionMatch = siteOptions.find(
								(option) => option.value === initialSelectedSiteValue
							);
							if (!optionMatch?.selectionKey) return null;
							return normalizedData.find(
								(site) =>
									`${site.site_id}:${site.cus_id ?? ""}` === optionMatch.selectionKey
							) || null;
						})();
				}
				const normalizedSelectedData = initialSelectedSiteData
					? await resolveCustomerIdForSite(normalizeSiteRecord(initialSelectedSiteData))
					: null;

				setSelectedSite(initialSelectedSiteValue);
				setSelectedSiteKey(
					normalizedSelectedData?.site_id
						? `${normalizedSelectedData.site_id}:${normalizedSelectedData.cus_id ?? ""}`
						: ""
				);
				setSelectedSiteData(normalizedSelectedData);
				setSelectedSiteLegalEnitity(
					normalizeSiteLegalEntity(normalizedSelectedData?.site_legal_entity)
				);
				setSelectedSiteWarehouse(normalizedSelectedData?.warehouse);
				setSelectedSiteSName(normalizedSelectedData?.site_name);
				const resolvedCurrency = normalizeSiteCurrency(normalizedSelectedData?.site_currency);
				const symbol = getCurrencySymbol(resolvedCurrency);
				const currency = resolvedCurrency.toLowerCase();
				setCurrencySymbol(symbol);
				setSelectedSiteCurrency(currency);
				localStorage.setItem("selectedSite", initialSelectedSiteValue);
				if (normalizedSelectedData?.site_id) {
					localStorage.setItem(
						"selectedSiteKey",
						`${normalizedSelectedData.site_id}:${normalizedSelectedData.cus_id ?? ""}`
					);
				}
			}
		} catch (error) {
			console.error("Failed to fetch sites:", error);
		} finally {
			setLoading(false);
		}
	};

	const updateSite = async (site, data, selectedOption = null) => {
		setSelectedSite(site);
		const resolvedSelectionKey =
			selectedOption?.selectionKey ||
			(selectedOption?.site_id ? `${selectedOption.site_id}:${selectedOption.cus_id ?? ""}` : "");
		setSelectedSiteKey(resolvedSelectionKey);
		const selectedSiteData =
			(selectedOption?.selectionKey
				? data.find(
						(s) => `${s.site_id}:${s.cus_id ?? ""}` === selectedOption.selectionKey
				  )
				: null) ||
			(selectedOption?.site_id
				? data.find(
						(s) =>
							Number(s.site_id) === Number(selectedOption.site_id) &&
							Number(s.cus_id ?? 0) === Number(selectedOption.cus_id ?? 0)
				  )
				: null) ||
			(() => {
				const matches = data.filter((s) => s.customer_site_number === site);
				return matches.length === 1 ? matches[0] : null;
			})();
		const normalizedSelectedData = selectedSiteData
			? await resolveCustomerIdForSite(normalizeSiteRecord(selectedSiteData))
			: null;
		setSelectedSiteData(normalizedSelectedData);

		setSelectedSiteLegalEnitity(
			normalizeSiteLegalEntity(normalizedSelectedData?.site_legal_entity)
		);
		setSelectedSiteWarehouse(normalizedSelectedData?.warehouse);
		setSelectedSiteSName(normalizedSelectedData?.site_name);
		const resolvedCurrency = normalizeSiteCurrency(normalizedSelectedData?.site_currency);
		const symbol = getCurrencySymbol(resolvedCurrency);
		const currency = resolvedCurrency.toLowerCase();
		setCurrencySymbol(symbol);
		setSelectedSiteCurrency(currency);
		localStorage.setItem("selectedSite", site); // Save the selected site to local storage
		if (normalizedSelectedData?.site_id) {
			localStorage.setItem(
				"selectedSiteKey",
				`${normalizedSelectedData.site_id}:${normalizedSelectedData.cus_id ?? ""}`
			);
		} else if (resolvedSelectionKey) {
			localStorage.setItem("selectedSiteKey", resolvedSelectionKey);
		} else if (normalizedSelectedData?.site_id) {
			localStorage.setItem(
				"selectedSiteKey",
				`${normalizedSelectedData.site_id}:${normalizedSelectedData.cus_id ?? ""}`
			);
		}
		if (
			pathnames.some((path) => !isNaN(path)) || // Check if any path segment is a number
			pathnames.includes("checkout") || // Check for "checkout"
			pathnames.includes("order-received") // Check for "order-received"
		) {
			navigate(ROUTES.MACHINES);
			return;
		}
	};

	const getSitePrice = (product) => {
		if (!product) return "";

		// Determine site currency — default USD
		const currency = (selectedSiteCurrency || "USD").toUpperCase();

		// Mapping product fields
		const priceFieldMap = {
			USD: "price_usd",
			CAD: "price_cad",
			GBP: "price_gbp",
			EUR: "price_eur",
			SGD: "price_sgd",
			INR: "price_inr",
		};

		const fieldName = priceFieldMap[currency] || "price_usd";

		// 1️⃣ Try product.price_xxx directly
		let rawPrice = product[fieldName];

		// 2️⃣ If not available → try product_meta
		if (!rawPrice && Array.isArray(product.product_meta)) {
			const meta = product.product_meta.find(m => m.meta_key === fieldName);
			rawPrice = meta?.meta_value;
		}

		// 3️⃣ Fallback to product_sell_price
		if (!rawPrice && product.product_sell_price) {
			rawPrice = product.product_sell_price;
		}

		// 4️⃣ Convert to number
		const numericPrice = parseFloat(rawPrice);
		if (isNaN(numericPrice)) return "";
		// console.log("Price: ", formatCurrency(numericPrice, currency))
		// 5️⃣ Format using your helper
		return formatCurrency(numericPrice, currency);
	};


	return (
		<SiteContext.Provider
			value={{
				selectedSite,
				selectedSiteKey,
				updateSite,
				siteOptions,
				loading,
				siteData,
				setLoggedinUser,
				selectedSiteData,
				selectedSiteCurrency,
				selectedSiteLegalEnitity,
				selectedSiteSName,
				selectedSiteWarehouse,
				currencySymbol,
				isAdminAllSites,
				fetchSites,
				getSitePrice
			}}>
			{children}
		</SiteContext.Provider>
	);
};
